import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, Trash2, LogOut, Image as ImgIcon, Mail, Maximize2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SPAN_OPTIONS = ["1x1", "2x1", "1x2", "2x2"];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || "";
      const base64 = String(result).split(",")[1] || "";
      resolve({ base64, mime: file.type || "image/jpeg" });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [tab, setTab] = useState("photos");
  const [photos, setPhotos] = useState([]);
  const [messages, setMessages] = useState([]);
  const [about, setAbout] = useState({ headline: "", body: "", portrait_base64: null, portrait_mime: null });
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const [p, m, a] = await Promise.all([
        api.get("/photos"),
        api.get("/messages"),
        api.get("/about"),
      ]);
      setPhotos(p.data || []);
      setMessages(m.data || []);
      setAbout({
        headline: a.data?.headline || "",
        body: a.data?.body || "",
        portrait_base64: a.data?.portrait_base64 || null,
        portrait_mime: a.data?.portrait_mime || null,
      });
    } catch (err) {
      // noop
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/", { replace: true });
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        if (!f.type.startsWith("image/")) continue;
        const { base64, mime } = await fileToBase64(f);
        await api.post("/photos/upload", { image_base64: base64, mime_type: mime, span: "1x1" });
      }
      toast.success(`Uploaded ${files.length} photo${files.length > 1 ? "s" : ""}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDelete = async (id) => {
    if (!confirm("Delete this photo?")) return;
    try {
      await api.delete(`/photos/${id}`);
      toast.success("Photo deleted");
      setPhotos((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const onChangeSpan = async (id, span) => {
    try {
      await api.patch(`/photos/${id}`, { span });
      setPhotos((prev) => prev.map((p) => (p.id === id ? { ...p, span } : p)));
      toast.success("Layout updated");
    } catch (err) {
      toast.error("Update failed");
    }
  };

  const onDeleteMsg = async (id) => {
    try {
      await api.delete(`/messages/${id}`);
      setMessages((prev) => prev.filter((m) => m.id !== id));
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const onSaveAbout = async () => {
    try {
      await api.put("/about", about);
      toast.success("About saved");
    } catch (err) {
      toast.error("Save failed");
    }
  };

  const onPortraitChange = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    const { base64, mime } = await fileToBase64(f);
    setAbout((prev) => ({ ...prev, portrait_base64: base64, portrait_mime: mime }));
  };

  return (
    <div data-testid="admin-dashboard" className="bg-zinc-100 min-h-[80vh]">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="font-anton uppercase text-5xl leading-none">Studio</h1>
            <div className="font-outfit text-sm text-zinc-600 mt-1">
              Hi {user?.name || "Srehan"} — manage your work.
            </div>
          </div>
          <button onClick={onLogout} data-testid="admin-logout-btn" className="brutal-btn">
            <LogOut size={16} strokeWidth={3} /> Logout
          </button>
        </div>

        {/* tabs */}
        <div className="flex gap-2 mb-6 border-b-2 border-black">
          {[
            { k: "photos", label: "Photos", icon: ImgIcon },
            { k: "messages", label: `Messages (${messages.length})`, icon: Mail },
            { k: "about", label: "About", icon: Maximize2 },
          ].map(({ k, label, icon: Icon }) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`admin-tab-${k}`}
              className={`inline-flex items-center gap-2 px-4 py-2 border-2 border-black border-b-0 font-anton uppercase tracking-wide -mb-[2px] ${
                tab === k ? "bg-black text-[#FFD600]" : "bg-white"
              }`}
            >
              <Icon size={14} strokeWidth={3} /> {label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {tab === "photos" && (
            <motion.div
              key="photos"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <div className="bg-white border-2 border-black p-6 mb-6 brutal-shadow-sm flex items-center justify-between flex-wrap gap-4">
                <div>
                  <div className="font-anton uppercase text-2xl">Upload photos</div>
                  <div className="text-sm text-zinc-600 font-outfit">
                    Drop in JPG / PNG. They appear on the gallery instantly.
                  </div>
                </div>
                <label className="brutal-btn cursor-pointer">
                  <Upload size={16} strokeWidth={3} />
                  {uploading ? "Uploading…" : "Choose Files"}
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={onUpload}
                    data-testid="admin-upload-input"
                    className="hidden"
                  />
                </label>
              </div>

              {photos.length === 0 ? (
                <div className="bg-white border-2 border-black p-10 text-center">
                  <div className="font-anton uppercase text-3xl text-zinc-400">No photos yet</div>
                  <div className="font-outfit text-sm text-zinc-600 mt-2">
                    Upload your first LEGO shot to get started.
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {photos.map((p) => (
                    <div
                      key={p.id}
                      data-testid={`admin-photo-${p.id}`}
                      className="border-2 border-black bg-white"
                    >
                      <div className="aspect-square bg-zinc-100 overflow-hidden border-b-2 border-black">
                        <img
                          src={`data:${p.mime_type};base64,${p.image_base64}`}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-3 flex items-center justify-between gap-2">
                        <select
                          value={p.span || "1x1"}
                          onChange={(e) => onChangeSpan(p.id, e.target.value)}
                          data-testid={`admin-span-${p.id}`}
                          className="border-2 border-black px-2 py-1 font-outfit text-sm bg-white"
                        >
                          {SPAN_OPTIONS.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => onDelete(p.id)}
                          data-testid={`admin-delete-${p.id}`}
                          className="inline-flex items-center justify-center p-2 border-2 border-black bg-white hover:bg-[#FF3B30] hover:text-white transition-colors"
                        >
                          <Trash2 size={14} strokeWidth={3} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {tab === "messages" && (
            <motion.div
              key="messages"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              {messages.length === 0 ? (
                <div className="bg-white border-2 border-black p-10 text-center">
                  <div className="font-anton uppercase text-3xl text-zinc-400">Inbox is empty</div>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((m) => (
                    <div key={m.id} data-testid={`admin-msg-${m.id}`} className="bg-white border-2 border-black p-5">
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                          <div className="font-anton uppercase text-xl">{m.name}</div>
                          <div className="text-sm text-zinc-600 font-outfit">{m.email}</div>
                        </div>
                        <button
                          onClick={() => onDeleteMsg(m.id)}
                          data-testid={`admin-msg-delete-${m.id}`}
                          className="inline-flex items-center justify-center p-2 border-2 border-black bg-white hover:bg-[#FF3B30] hover:text-white"
                        >
                          <Trash2 size={14} strokeWidth={3} />
                        </button>
                      </div>
                      <p className="mt-3 font-outfit whitespace-pre-line">{m.message}</p>
                      <div className="text-xs text-zinc-500 mt-3 font-outfit">{new Date(m.created_at).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {tab === "about" && (
            <motion.div
              key="about"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="bg-white border-2 border-black p-6 brutal-shadow-sm"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block mb-4">
                    <span className="font-anton uppercase text-sm tracking-widest mb-2 block">Headline</span>
                    <input
                      type="text"
                      value={about.headline}
                      onChange={(e) => setAbout({ ...about, headline: e.target.value })}
                      data-testid="admin-about-headline"
                      className="brutal-input"
                    />
                  </label>
                  <label className="block mb-4">
                    <span className="font-anton uppercase text-sm tracking-widest mb-2 block">Bio</span>
                    <textarea
                      rows={6}
                      value={about.body}
                      onChange={(e) => setAbout({ ...about, body: e.target.value })}
                      data-testid="admin-about-body"
                      className="brutal-input resize-none"
                    />
                  </label>
                  <label className="block mb-4">
                    <span className="font-anton uppercase text-sm tracking-widest mb-2 block">Portrait</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={onPortraitChange}
                      data-testid="admin-about-portrait-input"
                      className="font-outfit text-sm"
                    />
                  </label>
                  <button onClick={onSaveAbout} data-testid="admin-about-save" className="brutal-btn">
                    <Save size={16} strokeWidth={3} /> Save
                  </button>
                </div>
                <div>
                  {about.portrait_base64 ? (
                    <img
                      src={`data:${about.portrait_mime};base64,${about.portrait_base64}`}
                      alt=""
                      className="w-full h-[360px] object-cover border-2 border-black"
                    />
                  ) : (
                    <div className="w-full h-[360px] border-2 border-dashed border-black flex items-center justify-center font-anton uppercase text-xl text-zinc-400">
                      No Portrait Yet
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
